import { createContext, useContext } from 'react';

/**
 * وضع التركيز — **طلب المالك 2026-09-12** في المفكرة: «زر لما أضغطه يخفي كل شيء
 * بالشاشة باستثناء المحرر».
 *
 * القشرة تملك الحالة لأن أغلب ما يُخفى لها (القائمة الجانبية والشريط العلويّ
 * وأشرطة الحالة)، والشاشة تطلبه وتُخفي ما حول محرّرها بنفسها. فهو قدرةٌ من القشرة
 * لا ميزةُ مفكرة: أي شاشةٍ تحتاج صفحةً بيضاء تطلبه كما هو.
 *
 * و**الإخفاء إزالةٌ من التدفّق لا غطاءٌ فوقه**. طبقةٌ ثابتة (`fixed inset-0`)
 * فوق القشرة كانت تحتاج أحد أمرين: نقلَ المحرّر إليها — فيُعاد تركيب TipTap
 * ويضيع سجلّ التراجع وموضع المؤشر — أو الاطمئنانَ إلى أن لا سلفَ يُنشئ سياق
 * تكديسٍ يحبسها تحت الشريط الجانبي. والإزالة لا تحتاج شيئًا من ذلك: المحرّر في
 * موضعه من الشجرة لا يُفكّ ولا يُركَّب، والصفحة تتمرّر كعادتها على الجوال.
 */
export interface FocusModeValue {
  readonly active: boolean;
  readonly setActive: (active: boolean) => void;
}

/** خارج القشرة (شاشة الدخول، الاختبارات) لا وضع تركيز — والطلب لا يفعل شيئًا بدل أن يرمي. */
export const FocusModeContext = createContext<FocusModeValue>({
  active: false,
  setActive: () => undefined,
});

export function useFocusMode(): FocusModeValue {
  return useContext(FocusModeContext);
}

export interface FocusKey {
  readonly key: string;
  readonly isComposing: boolean;
  readonly defaultPrevented: boolean;
}

/**
 * أيُخرج هذا المفتاح من وضع التركيز؟ — Escape وحده، وبثلاثة استثناءات يمنع كلٌّ
 * منها خروجًا في غير وقته يُسقط الصفحة البيضاء وسط جملة:
 *
 * - **أثناء التركيب** (لوحات IME): Escape هناك يُلغي الاقتراح لا الوضع.
 * - **حدثٌ عالجه من سبقنا** (`defaultPrevented`): مستمعٌ أسبق منّا في طور
 *   الالتقاط أغلق به ما يخصّه. وهذا الاستثناء **لا يشمل المحرّر**: القشرة
 *   تستمع في طور الالتقاط لأن ProseMirror يستهلك Escape ويستدعي
 *   `preventDefault` — فالاستماع في الفقاعة كان يعني ألّا يخرج المالك أبدًا
 *   وهو يكتب. مقيسٌ في متصفح: `defaultPrevented` كان `false` في الالتقاط
 *   و`true` في الفقاعة للضغطة نفسها.
 * - **حوارٌ مفتوح فوقه**: Escape لوحة الالتقاط يغلقها هي لا الوضع تحتها.
 */
export function focusModeKeyAction(event: FocusKey, dialogOpen: boolean): 'exit' | 'ignore' {
  if (event.key !== 'Escape') return 'ignore';
  if (event.isComposing || event.defaultPrevented || dialogOpen) return 'ignore';
  return 'exit';
}
